import React, { useEffect, useState } from 'react';
import { Loader2, CalendarClock, ShieldAlert } from 'lucide-react';
import api, { errText } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import ConsultationBoard from '../../components/ConsultationBoard';
import ConsultationCalendar from '../../components/ConsultationCalendar';
import SessionTypesEditor from '../../components/SessionTypesEditor';
import AvailabilityEditor from '../../components/AvailabilityEditor';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

export const DEFAULT_SESSION_TYPES = [
  { name: '15 minute guidance', duration_min: 15, price: 20, description: '' },
  { name: '30 minute guidance', duration_min: 30, price: 30, description: '' },
  { name: 'Meditation class', duration_min: 75, price: 45, description: '' },
];

const OfferEditor = ({ teacher, agreement, onSaved }) => {
  const { toast } = useToast();
  const [f, setF] = useState({
    offers_private_lessons: !!teacher.offers_private_lessons,
    session_types: (teacher.session_types && teacher.session_types.length) ? teacher.session_types : DEFAULT_SESSION_TYPES,
    private_lesson_currency: teacher.private_lesson_currency || 'usd',
    private_lesson_note: teacher.private_lesson_note || '',
    private_availability_note: teacher.private_availability_note || '',
    agreement_signature: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const alreadyAgreed = !!teacher.facilitator_agreement_accepted_at;
  const needAgreement = f.offers_private_lessons && !alreadyAgreed;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f,
        session_types: (f.session_types || []).filter((s) => (s.name || '').trim()).map((s) => ({
          ...(s.id ? { id: s.id } : {}), name: s.name.trim(),
          duration_min: s.duration_min === '' || s.duration_min == null ? null : parseInt(s.duration_min, 10),
          price: s.price === '' || s.price == null ? 0 : parseFloat(s.price),
          description: (s.description || '').trim() || null,
        })),
      };
      const r = await api.patch(`/consultations/my-offer/${teacher.id}`, payload);
      toast({ title: 'Saved' }); onSaved(r.data);
    } catch (e) { toast({ title: 'Could not save', description: errText(e) }); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-3xl bg-white/70 border border-rose/40 p-5" data-testid="offer-editor">
      <h3 className="font-serif-display text-xl text-burgundy flex items-center gap-2"><CalendarClock className="w-5 h-5 text-rose-italic" /> {teacher.name} — private sessions</h3>
      {teacher.private_lesson_status && teacher.private_lesson_status !== 'none' && (
        <p className="mt-1 text-xs" data-testid="offer-status">
          Status: <span className={`px-2 py-0.5 rounded-full ${teacher.private_lesson_status === 'approved' ? 'bg-green-100 text-green-700' : teacher.private_lesson_status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'}`}>{teacher.private_lesson_status}</span>
          {teacher.private_lesson_review_note ? <span className="text-mahogany/60"> — {teacher.private_lesson_review_note}</span> : null}
          {teacher.private_lesson_status === 'pending' && <span className="text-mahogany/60"> (awaiting admin approval)</span>}
        </p>
      )}
      <label className="mt-3 flex items-center gap-2 text-sm text-mahogany"><input type="checkbox" checked={f.offers_private_lessons} onChange={(e) => set('offers_private_lessons', e.target.checked)} data-testid="offer-enable" /> Offer private lessons / consultations</label>
      {f.offers_private_lessons && (
        <div className="mt-3 space-y-3">
          <SessionTypesEditor value={f.session_types} onChange={(v) => set('session_types', v)} currency={f.private_lesson_currency} />
          <input value={f.private_lesson_note} onChange={(e) => set('private_lesson_note', e.target.value)} placeholder="Short note (what you offer)" className="w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" />
          <input value={f.private_availability_note} onChange={(e) => set('private_availability_note', e.target.value)} placeholder="Availability, e.g. Weekday evenings IST" className="w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="offer-availability" />
          <p className="text-xs text-mahogany/60">For paid options you receive the price minus the platform commission; the client's card is authorized at booking and captured only when you accept.</p>
          {needAgreement && (
            <div className="rounded-2xl bg-rose-soft/30 border border-rose/40 p-4">
              <div className="flex items-center gap-2 text-burgundy font-medium mb-2 text-sm"><ShieldAlert className="w-4 h-4" /> Facilitator agreement</div>
              <p className="text-xs text-mahogany/75 leading-relaxed max-h-32 overflow-y-auto pr-1">{agreement}</p>
              <input value={f.agreement_signature} onChange={(e) => set('agreement_signature', e.target.value)} placeholder="Type your full name to accept" className="mt-2 w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="offer-signature" />
            </div>
          )}
          {alreadyAgreed && <p className="text-xs text-green-700">Agreement accepted on {new Date(teacher.facilitator_agreement_accepted_at).toLocaleDateString()}.</p>}
        </div>
      )}
      <button onClick={save} disabled={saving} className="pill-btn mt-4" data-testid="offer-save">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
    </div>
  );
};

const ConsultationRequests = () => {
  const { toast } = useToast();
  const [teachers, setTeachers] = useState([]);
  const [items, setItems] = useState([]);
  const [agreement, setAgreement] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [mine, reqs, ag] = await Promise.all([
        api.get('/teachers/mine').then((r) => r.data || []).catch(() => []),
        api.get('/consultations/mine').then((r) => r.data || []).catch(() => []),
        api.get('/consultations/facilitator-agreement').then((r) => r.data.text).catch(() => ''),
      ]);
      setTeachers(mine.filter((t) => t.user_id));
      setItems(reqs); setAgreement(ag);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = (fn) => async (...a) => { try { await fn(...a); load(); } catch (e) { toast({ title: 'Action failed', description: errText(e) }); } };
  const onAccept = act((id) => api.post(`/consultations/${id}/accept`));
  const onReject = act((id) => api.post(`/consultations/${id}/reject`));
  const onPropose = act((id, when) => api.post(`/consultations/${id}/propose`, { proposed_datetime: when }));

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-mahogany" /></div>;

  return (
    <div className="space-y-6" data-testid="consultation-requests-page">
      <h1 className="font-serif-display text-3xl text-burgundy">Private sessions</h1>
      {teachers.length === 0 && <p className="text-mahogany/70">Link or create a facilitator profile to offer private sessions.</p>}
      <Tabs defaultValue="definitions" className="w-full">
        <TabsList data-testid="consultation-tabs">
          <TabsTrigger value="definitions" data-testid="tab-definitions">Session definitions</TabsTrigger>
          <TabsTrigger value="availability" data-testid="tab-availability">Availability</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">Calendar view</TabsTrigger>
        </TabsList>
        <TabsContent value="definitions" className="mt-5 space-y-6">
          <div className="space-y-4">{teachers.map((t) => <OfferEditor key={t.id} teacher={t} agreement={agreement} onSaved={() => load()} />)}</div>
          <div className="pt-4 border-t border-rose/30">
            <ConsultationBoard items={items} onAccept={onAccept} onReject={onReject} onPropose={onPropose} />
          </div>
        </TabsContent>
        <TabsContent value="availability" className="mt-5 space-y-6">
          {teachers.map((t) => <AvailabilityEditor key={t.id} teacher={t} onSaved={() => load()} />)}
        </TabsContent>
        <TabsContent value="calendar" className="mt-5">
          <ConsultationCalendar items={items} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ConsultationRequests;
